import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Award, Clock, Star, FileCheck, CheckCircle, Lightbulb } from "lucide-react";

const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const stats = [
    { icon: Award, value: "10+", label: "Års erfarenhet" },
    { icon: Star, value: "4,9", label: "I betyg" },
    { icon: Clock, value: "100%", label: "Håller tider" },
    { icon: FileCheck, value: "100%", label: "Tydliga offerter" },
  ];

  const features = [
    { icon: CheckCircle, text: "Extrem noggrannhet i varje projekt" },
    { icon: CheckCircle, text: "Lösningsorienterade och flexibla" },
    { icon: Lightbulb, text: "Designmedvetna elinstallationer" },
  ];

  return (
    <section
      id="om-oss"
      data-testid="about-section"
      ref={ref}
      className="py-24 md:py-32 bg-[#050505]"
    >
      <div className="container mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Column - Text Content */}
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4"
            >
              Om Optimal EL
            </motion.p>

            <motion.h2
              data-testid="about-title"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-3xl md:text-4xl lg:text-5xl font-manrope font-light text-white mb-6 tracking-tight"
            >
              Vi sätter standarden
              <br />
              <span className="text-zinc-500">för kvalitet.</span>
            </motion.h2>

            <motion.p
              data-testid="about-description"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-zinc-400 text-base md:text-lg leading-relaxed mb-8"
            >
              Med över ett decennium av erfarenhet har Optimal EL STHLM etablerat sig 
              som Stockholms ledande val för premium elinstallationer och ljusdesign. 
              Vi kombinerar teknisk expertis med estetisk känsla för att leverera 
              resultat som överträffar förväntningar.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="space-y-4"
            >
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <feature.icon className="text-[#D4AF37] w-5 h-5 flex-shrink-0" />
                  <span className="text-zinc-300">{feature.text}</span>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right Column - Stats Grid */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid grid-cols-2 gap-4"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                data-testid={`about-stat-${index}`}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                className="bg-[#0A0A0A] border border-white/5 p-6 md:p-8 group hover:border-[#D4AF37]/30 transition-all duration-500"
              >
                <stat.icon className="w-8 h-8 text-[#D4AF37] mb-4 group-hover:scale-110 transition-transform duration-300" />
                <div className="text-3xl md:text-4xl font-manrope font-light text-white mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-zinc-500 uppercase tracking-wider">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
